import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useWallet } from '../../context/WalletContext';
import { useNotification } from '../../context/NotificationContext';
import { formatXPUNT } from '../../utils/helpers';
import {
  FaHome, FaTicketAlt, FaTrophy, FaHandshake, FaUser,
  FaWallet, FaBell, FaCog, FaSignOutAlt, FaChartLine,
  FaCrown, FaStar, FaBars, FaTimes,
  FaTachometerAlt, FaUsers, FaShieldAlt, FaRocket,
  FaChevronLeft, FaChevronRight, FaSearch,
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import './DashboardLayout.css';
import MobileBottomNav from '../layout/MobileBottomNav';
import MobileQuickPanel from '../layout/MobileQuickPanel';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const { balance } = useWallet();
  const { unreadCount } = useNotification();
  const location = useLocation();
  const navigate = useNavigate();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quickPanelOpen, setQuickPanelOpen] = useState(false);

  // ==================== HANDLERS ====================
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  // ==================== USER FLAGS ====================
  const isPunterPlusActive =
    user?.punter_plus && user?.punter_plus_status !== 'expired';
  const isAdmin = user?.capabilities?.manage_options || false;

  // ==================== NAV DATA ====================
  const navItems = [
    { path: '/dashboard',             label: 'Dashboard',     icon: FaTachometerAlt },
    { path: '/bet-codes',             label: 'Bet Codes',     icon: FaTicketAlt },
    { path: '/competitions',          label: 'Competitions',  icon: FaTrophy },
    { path: '/virtual-competitions',  label: 'Virtual Comps', icon: FaTrophy },
    { path: '/h2h',                   label: 'H2H Game',      icon: FaHandshake },
    { path: '/virtual-h2h',           label: 'Virtual H2H',   icon: FaUsers },
    { path: '/rollover',              label: 'Rollover',      icon: FaChartLine },
    { path: '/leaderboard',           label: 'Leaderboard',   icon: FaChartLine },
    { path: '/explore',               label: 'Explore',       icon: FaUsers },
  ];

  const bottomItems = [
    { path: '/wallet',        label: 'Wallet',        icon: FaWallet },
    { path: '/profile',       label: 'Profile',       icon: FaUser },
    { path: '/settings',      label: 'Settings',      icon: FaCog },
    { path: '/notifications', label: 'Notifications', icon: FaBell, badge: unreadCount },
  ];

  // ==================== RENDER ====================
  return (
    <div className="dashboard-layout">
      {/* ==================== TOP BAR ==================== */}
      <header className="dashboard-topbar">
        <div className="topbar-left">
          <button
            className="sidebar-toggle"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            type="button"
            aria-label="Toggle sidebar"
          >
            {sidebarCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>

          <div className="topbar-logo">
            <span className="logo-icon">
              <img src="/logo.png" alt="XPunters" style={{ width: 30, height: 30 }} />
            </span>
            <span className="logo-text">XPunters</span>
          </div>
        </div>

        <div className="topbar-center">
          <div className="topbar-search">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search..."
              className="search-input"
            />
          </div>
        </div>

        <div className="topbar-right">
          {/* Admin */}
          {isAdmin && (
            <Link to="/admin" className="topbar-btn admin-btn" title="Admin Dashboard">
              <FaShieldAlt />
            </Link>
          )}

          {/* Punter+ / Upgrade */}
          <Link to="/subscription" className="topbar-btn punter-btn">
            {isPunterPlusActive ? (
              <>
                <FaCrown className="crown-icon" />
                <span className="punter-label">Punter+</span>
              </>
            ) : (
              <>
                <FaStar className="star-icon" />
                <span className="punter-label">Upgrade</span>
              </>
            )}
          </Link>

          {/* Wallet */}
          <Link to="/wallet" className="topbar-btn wallet-btn">
            <FaWallet />
            <span className="wallet-balance">{formatXPUNT(balance)}</span>
          </Link>

          {/* Notifications */}
          <Link to="/notifications" className="topbar-btn notification-btn">
            <FaBell />
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
          </Link>

          {/* Avatar */}
          <div className="topbar-user">
            <button
              className="user-avatar-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              type="button"
              aria-label="Open account menu"
            >
              {user?.display_name?.[0] || 'U'}
            </button>
          </div>

          {/* Mobile menu toggle */}
          <button
            className="mobile-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            type="button"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </header>

      {/* ==================== BODY ==================== */}
      <div className="dashboard-body">
        {/* Sidebar */}
        <aside className={`dashboard-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <nav className="sidebar-nav">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
              >
                <item.icon className="sidebar-icon" />
                <span className="sidebar-label">{item.label}</span>
              </Link>
            ))}
          </nav>

          <div className="sidebar-divider" />

          <nav className="sidebar-nav bottom">
            {bottomItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
              >
                <item.icon className="sidebar-icon" />
                <span className="sidebar-label">{item.label}</span>
                {item.badge > 0 && (
                  <span className="sidebar-badge">{item.badge}</span>
                )}
              </Link>
            ))}

            {isPunterPlusActive && (
              <Link to="/creator/dashboard" className="sidebar-link creator-link">
                <FaRocket className="sidebar-icon" />
                <span className="sidebar-label">Creator</span>
              </Link>
            )}

            <button
              onClick={handleLogout}
              className="sidebar-link logout-link"
              type="button"
            >
              <FaSignOutAlt className="sidebar-icon" />
              <span className="sidebar-label">Logout</span>
            </button>
          </nav>
        </aside>

        {/* Main content */}
        <main className="dashboard-content">
          <div className="content-wrapper">
            <Outlet />
          </div>
        </main>
      </div>

      {/* ==================== MOBILE DRAWER (account actions) ==================== */}
      {mobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <div className={`mobile-dashboard-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-header">
          <div className="mobile-user-info">
            <div className="mobile-user-avatar">
              {user?.display_name?.[0] || 'U'}
            </div>
            <div className="mobile-user-details">
              <span className="mobile-user-name">{user?.display_name || 'User'}</span>
              <span className="mobile-user-email">{user?.email || ''}</span>
            </div>
          </div>
        </div>

        <nav className="mobile-menu-nav">
          {bottomItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`mobile-menu-link ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <item.icon className="mobile-menu-icon" />
              <span>{item.label}</span>
              {item.badge > 0 && (
                <span className="mobile-menu-badge">{item.badge}</span>
              )}
            </Link>
          ))}

          {isPunterPlusActive && (
            <Link
              to="/creator/dashboard"
              className="mobile-menu-link creator-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              <FaRocket className="mobile-menu-icon" />
              <span>Creator Dashboard</span>
            </Link>
          )}

          {isAdmin && (
            <Link
              to="/admin"
              className="mobile-menu-link admin-link"
              onClick={() => setMobileMenuOpen(false)}
            >
              <FaShieldAlt className="mobile-menu-icon" />
              <span>Admin Dashboard</span>
            </Link>
          )}

          <button
            onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
            className="mobile-menu-link logout-link"
            type="button"
          >
            <FaSignOutAlt className="mobile-menu-icon" />
            <span>Logout</span>
          </button>
        </nav>
      </div>

      {/* ==================== MOBILE BOTTOM NAV (curved + FAB) ==================== */}
      <MobileBottomNav
        quickOpen={quickPanelOpen}
        onQuickToggle={() => setQuickPanelOpen((open) => !open)}
      />

      {/* ==================== MOBILE QUICK PANEL (grid overlay) ==================== */}
      <MobileQuickPanel
        open={quickPanelOpen}
        onClose={() => setQuickPanelOpen(false)}
        user={user}
        isAdmin={isAdmin}
        isPunterPlusActive={isPunterPlusActive}
      />
    </div>
  );
};

export default DashboardLayout;